import type {
  AuthRole,
  LoginRequest,
  LoginResponse,
  VerifyRequest,
  VerifyResponse,
  AuthError,
} from '../types/auth';
import { read_json_body, http_error_message } from '../utils/http';

const API_BASE_URL = '/api/auth';

/** Marks the errors we throw on purpose, so real exceptions are not mistaken for them */
const is_auth_error = (error: unknown): error is AuthError =>
  typeof error === 'object' && error !== null && 'message' in error && 'status' in error;

/** Client logins hit /api/auth/login, admin logins hit /api/auth/admin/login */
const login_endpoint = (role: AuthRole): string =>
  role === 'admin' ? `${API_BASE_URL}/admin/login` : `${API_BASE_URL}/login`;

/**
 * Login with a username and password
 * @param username - Account username
 * @param password - Account password
 * @param role - Which credential set to authenticate against (defaults to 'client')
 * @returns Login response with JWT token
 * @throws AuthError if login fails
 */
export const loginWithPassword = async (
  username: string,
  password: string,
  role: AuthRole = 'client'
): Promise<LoginResponse> => {
  try {
    const request: LoginRequest = { username, password };

    const response = await fetch(login_endpoint(role), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await read_json_body<LoginResponse & { error?: string }>(response);

    if (!response.ok || !data) {
      const error: AuthError = {
        message: data?.error || http_error_message(response, data !== null),
        status: response.status,
      };
      throw error;
    }

    return data as LoginResponse;
  } catch (error) {
    if (is_auth_error(error)) {
      throw error;
    }

    const authError: AuthError = {
      message: 'Network error. Please try again.',
      status: 500,
    };
    throw authError;
  }
};

/**
 * Verify JWT token
 * @param token - JWT token to verify
 * @returns Verification response
 * @throws AuthError if verification fails
 */
export const verifyToken = async (token: string): Promise<VerifyResponse> => {
  try {
    const request: VerifyRequest = { token };

    const response = await fetch(`${API_BASE_URL}/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await read_json_body<VerifyResponse & { error?: string }>(response);

    if ((!response.ok && response.status !== 401) || !data) {
      const error: AuthError = {
        message: data?.error || http_error_message(response, data !== null),
        status: response.status,
      };
      throw error;
    }

    return data as VerifyResponse;
  } catch (error) {
    if (is_auth_error(error)) {
      throw error;
    }

    const authError: AuthError = {
      message: 'Network error. Please try again.',
      status: 500,
    };
    throw authError;
  }
};

/**
 * Logout user (client-side only)
 * Clears stored token
 */
export const logout = (): void => {
  // This is handled in the auth context
  // We'll clear the localStorage there
  return;
};
